import { EventEmitter, Injectable } from '@angular/core';
import {
	CreatedShare,
	ErrorResponse,
	Experience,
	ExperienceFetchTypes,
	Export,
	GameData,
	ShareExperienceRequest,
} from '@meta-press/shared-plugins';
import { catchError, forkJoin, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { LoginService } from '../login-service/login.service';
import { ShareProcess } from '../exports/exports.component';
import { BackendService } from '../backend-service/backend.service';
import { SettingsService } from '../services/settings-service/settings.service';
import { SourceMaterialService } from '../source-material-service/source-material.service';
import { NotificationService } from '../notification-service/notification.service';

/** Service for managing, providing and sharing user-created exports */
@Injectable({
	providedIn: 'root',
})
export class ExportService {
	exports: Export[] = [];
	sharedExperiences: CreatedShare[] = [];

	/** Map to track archived state of exports (key: export ID, value: archived state) */
	private archivedStates = new Map<number, boolean>();

	public OnExportsChanged: EventEmitter<Export[]> = new EventEmitter<
		Export[]
	>();

	public OnSharedExperiencesChanged: EventEmitter<CreatedShare[]> =
		new EventEmitter<CreatedShare[]>();

	public get Exports(): Export[] {
		return this.exports;
	}

	public get SharedExperiences(): CreatedShare[] {
		return this.sharedExperiences;
	}

	private isLoading = false;
	public get IsLoading(): boolean {
		return this.isLoading;
	}

	private error = '';
	public get Error(): string {
		return this.error;
	}

	private hasLoadedArchivedExports = false;
	private isLoadingArchivedExports = false;
	public get IsLoadingArchivedExports(): boolean {
		return this.isLoadingArchivedExports;
	}

	/**
	 * Check if an export is archived
	 * @param exportId The ID of the export to check
	 * @returns True if the export is archived, false otherwise
	 */
	public isExportArchived(exportId: number): boolean {
		return this.archivedStates.get(exportId) ?? false;
	}

	/**
	 * Set the archived state of an export
	 * @param exportId The ID of the export
	 * @param archived The archived state to set
	 */
	public setExportArchived(exportId: number, archived: boolean): void {
		this.archivedStates.set(exportId, archived);
	}

	constructor(
		private backendService: BackendService,
		private loginService: LoginService,
		private settingsService: SettingsService,
		private sourceMaterialService: SourceMaterialService,
		private notificationService: NotificationService,
	) {
		this.loginService.OnLogout.subscribe(() => {
			// Clear exports on logout
			this.exports = [];
			this.sharedExperiences = [];
			this.archivedStates.clear();
			this.OnExportsChanged.emit(this.exports);
			this.isLoading = false;
			this.error = '';
		});

		// this.LoadDemoExports();
		this.loginService.OnLogin.subscribe((user) => {
			if (user) {
				this.isLoading = true;
				this.error = '';
				// this.dataService.GetAllExperiences().pipe(
				// 	map((experiences: Experience[]) => {
				// 		console.log('Experiences:', experiences);
				// 		experiences.forEach((exp: Experience) => {
				// 			if (exp) {
				// 				this.AddExportToLocal({
				// 					name: exp.name,
				// 					id: exp.id,
				// 					uuid: exp.uuid,
				// 					date: exp.createdUnixTime,
				// 					creator: exp.userId,
				// 					gameData: JSON.parse(exp.jsonBlob) as GameData,
				// 				} as Export);
				// 			}
				// 		});
				// 		return experiences;
				// 	}),
				// 	catchError((error) => {
				// 		console.error('Error fetching experiences:', error);
				// 		return throwError(() => new Error('Error fetching experiences: ' + error));
				// 	})
				// ).subscribe();

				// Use forkJoin to execute both API calls in parallel

				forkJoin({
					experiences: this.backendService.GetAllExperiences(
						ExperienceFetchTypes.NonArchived,
					),
					sharedExperiences:
						this.backendService.GetSharedExperiences(),
				})
					.pipe(
						catchError((error) => {
							console.error(
								`Virhe latauksessa: ${error.status}, ${error.statusText}, ${error.error}`,
							);
							this.isLoading = false;
							return throwError(() => new Error(error));
						}),
					)
					.subscribe({
						next: (results) => {
							// Process experiences
							console.log('Experiences:', results.experiences);
							this.exports = [];

							results.experiences.forEach((exp: Experience) => {
								if (exp) {
									const gameData = JSON.parse(exp.jsonBlob) as GameData;
									GameData.restoreMaps(null, gameData);
									this.AddExportToLocal(
										{
											name: exp.name,
											id: exp.id,
											uuid: exp.uuid,
											date: exp.createdUnixTime,
											creator: exp.userId,
											gameData: gameData,
										} as Export,
										exp.archived,
									);
								}
							});

							// Process shared experiences
							console.log(
								'Shared Experiences:',
								results.sharedExperiences,
							);
							this.sharedExperiences =
								results.sharedExperiences as CreatedShare[];

							// Update UI state
							this.isLoading = false;
							this.OnExportsChanged.emit(this.exports);
							this.OnSharedExperiencesChanged.emit(
								this.sharedExperiences,
							);
						},
						error: (error) => {
							console.error('Error in forkJoin:', error);
							this.isLoading = false;
							this.error =
								'Virhe kokemusten hakemisessa: ' + error;
						},
					});
			}
		});
	}

	DeleteShare(shareId: number): Observable<void> {
		return this.backendService.DeleteShare(shareId).pipe(
			map(() => {
				console.log(`Share ${shareId} deleted successfully`);

				this.sharedExperiences = this.sharedExperiences.filter(
					(share) => share.id !== shareId,
				);
				this.OnSharedExperiencesChanged.emit(this.sharedExperiences);
				return undefined;
			}),
			catchError((error: any) => {
				console.error(`Error deleting share ${shareId}:`, error);
				return throwError(() => error);
			}),
		);
	}

	GetSharedExperiencesForExport(exportId: number): CreatedShare[] {
		return this.sharedExperiences.filter(
			(sharedExperience) => sharedExperience.experienceId === exportId,
		);
	}

	RefreshExports(): Observable<Export[]> {
		return this.backendService
			.GetAllExperiences(ExperienceFetchTypes.NonArchived)
			.pipe(
				map((experiences: Experience[]) => {
					console.log('Experiences:', experiences);
					experiences.forEach((exp: Experience) => {
						if (exp) {
							const gameData = JSON.parse(exp.jsonBlob) as GameData;
							GameData.restoreMaps(null, gameData);
							this.AddExportToLocal(
								{
									name: exp.name,
									version: exp.version,
									id: exp.id,
									uuid: exp.uuid,
									date: exp.createdUnixTime,
									creator: exp.userId,
									gameData: gameData,
								} as Export,
								exp.archived,
							);
						}
					});

					this.OnExportsChanged.emit(this.exports);

					return this.exports;
				}),
			);
	}

	/**
	 * Load archived exports from the server and merge them into the current exports array.
	 * This method will only load archived exports once. Subsequent calls will be ignored.
	 * Exports with the same UUID will be overwritten, new exports will be added.
	 */
	loadArchivedExports(): void {
		// Check if we've already loaded archived exports
		if (this.hasLoadedArchivedExports) {
			console.log(
				'[loadArchivedExports] Archived exports already loaded, skipping',
			);
			return;
		}

		// Check if we're already loading
		if (this.isLoadingArchivedExports) {
			console.log(
				'[loadArchivedExports] Already loading archived exports, skipping',
			);
			return;
		}

		console.log('[loadArchivedExports] Starting to load archived exports');
		this.isLoadingArchivedExports = true;

		// Fetch archived exports from the backend
		this.backendService
			.GetAllExperiences(ExperienceFetchTypes.Archived)
			.subscribe({
				next: (experiences: Experience[]) => {
					console.log(
						'[loadArchivedExports] Received archived experiences:',
						experiences.length,
					);

					// Merge archived exports into the current exports array
					experiences.forEach((exp: Experience) => {
						if (exp) {
							const gameData = JSON.parse(exp.jsonBlob) as GameData;
							GameData.restoreMaps(null, gameData);
							this.AddExportToLocal(
								{
									name: exp.name,
									version: exp.version,
									id: exp.id,
									uuid: exp.uuid,
									date: exp.createdUnixTime,
									creator: exp.userId,
									gameData: gameData,
								} as Export,
								exp.archived,
							);
						}
					});

					// Emit changes so components can update
					this.OnExportsChanged.emit(this.exports);

					// Mark as loaded
					this.hasLoadedArchivedExports = true;
					this.isLoadingArchivedExports = false;
					console.log(
						'[loadArchivedExports] Successfully loaded archived exports',
					);
				},
				error: (error: unknown) => {
					console.error(
						'[loadArchivedExports] Error loading archived exports:',
						error,
					);
					this.isLoadingArchivedExports = false;
					// Don't mark as loaded so user can retry
				},
			});
	}

	DeleteExportFromLocal(id: number) {
		console.log('[DeleteExportFromLocal] Deleting export ID:', id);
		if (id > -1) {
			const index: number = this.exports.findIndex((e) => e.id === id);
			console.log('[DeleteExportFromLocal] Found at index:', index);
			if (index > -1) {
				this.exports.splice(index, 1);
				console.log(
					'[DeleteExportFromLocal] Emitting OnExportsChanged, remaining exports:',
					this.exports.length,
				);
				// Emit event so components can react to the change
				this.OnExportsChanged.emit(this.exports);
			}
		}
	}

	/**
	 * Remove all shares for a specific export from local state
	 * Used when backend cascade-deletes shares
	 */
	RemoveSharesForExport(exportId: number) {
		const initialLength = this.sharedExperiences.length;
		this.sharedExperiences = this.sharedExperiences.filter(
			(share) => share.experienceId !== exportId,
		);
		// Only emit if something was actually removed
		if (this.sharedExperiences.length !== initialLength) {
			this.OnSharedExperiencesChanged.emit(this.sharedExperiences);
		}
	}
	ClearExports() {
		this.exports = [];
		this.archivedStates.clear();
	}

	private AddExportToLocal(EXPORT: Export, archived?: boolean) {
		const index: number = this.exports.findIndex(
			(e) => e.uuid && EXPORT.uuid && e.uuid === EXPORT.uuid,
		);
		if (index > -1) {
			this.exports[index] = EXPORT;
		} else {
			this.exports.push(EXPORT);
		}

		// Store archived state in map if export has valid ID
		if (EXPORT.id && EXPORT.id > -1) {
			this.setExportArchived(EXPORT.id, archived ?? false);
		}
	}

	public SaveExport(EXPORT: Export): Observable<Export> {
		// Override existing name and other information
		// const currentUser = this.loginService.CurrentUser;
		// if (currentUser) {
		// 	EXPORT.creator = currentUser.id;
		// 	EXPORT.
		// }

		// Add to database
		return this.backendService
			.AddNewExperience(
				EXPORT.name,
				JSON.stringify(EXPORT.gameData),
				this.settingsService.AppDataVersion,
			)
			.pipe(
				catchError((error: any) => {
					console.error('Error adding export to database:', error);
					throw error;
				}),
				map((response: any) => {
					EXPORT.id = response;

					// Add local user info because we don't load this from server at this point
					const currentUser = this.loginService.CurrentUser;
					if (currentUser) {
						EXPORT.creator = currentUser.id;
					}

					this.exports.push(EXPORT);

					// Set archived state to false for new exports
					if (EXPORT.id && EXPORT.id > -1) {
						this.setExportArchived(EXPORT.id, false);
					}

					return EXPORT;
				}),
			);
	}

	public UpdateExport(
		EXPORT: Export,
		archived?: boolean,
	): Observable<Export> {
		if (!EXPORT.id || EXPORT.id === -1) {
			throw new Error('Cannot update export without valid ID');
		}

		// Get archived state from map or use provided value
		const archivedState =
			archived !== undefined
				? archived
				: this.isExportArchived(EXPORT.id);

		// Create experience request object
		const experienceRequest = {
			Id: EXPORT.id,
			Name: EXPORT.name,
			JsonBlob: JSON.stringify(EXPORT.gameData),
			Version: 1,
			Archived: archivedState,
		};

		// Update in database
		return this.backendService.UpdateExperience(experienceRequest).pipe(
			catchError((error: any) => {
				console.error('Error updating export in database:', error);
				throw error;
			}),
			map(() => {
				// Update local export
				const index = this.exports.findIndex((e) => e.id === EXPORT.id);
				if (index > -1) {
					this.exports[index] = EXPORT;
				}

				// Update archived state in map
				if (EXPORT.id && EXPORT.id > -1) {
					this.setExportArchived(EXPORT.id, archivedState);
				}

				// Emit changes
				this.OnExportsChanged.emit(this.exports);
				return EXPORT;
			}),
			switchMap((updatedExport: Export) => {
				// Check if export has a primary share
				const primaryShare = this.getPrimaryShare(updatedExport.id);

				if (!primaryShare) {
					// No share exists, just return the updated export
					return of(updatedExport);
				}

				// Validate gameData exists
				if (!updatedExport.gameData) {
					console.warn(
						`Cannot update share: export ${updatedExport.id} has no gameData`,
					);
					return of(updatedExport);
				}

				// Share exists - automatically update it with new gameData
				console.log(
					`Automatically updating share for export ${updatedExport.id}`,
				);

				// Extract article IDs from updated gameData
				const usedArticleIds = this.extractUsedArticleIds(
					updatedExport.gameData,
				);

				// Update the share
				return this.updateSharedExperience(
					primaryShare,
					updatedExport,
					usedArticleIds,
				).pipe(
					tap(() => {
						// Show success notification
						this.notificationService.ShowToast(
							'Peli ja julkaisu päivitetty onnistuneesti',
							'success',
						);
					}),
					map(() => updatedExport), // Return the export regardless of share update
					catchError((shareError: unknown) => {
						// Log share update error but don't fail the export update
						console.error(
							'Error updating share:',
							shareError,
						);
						this.notificationService.ShowToast(
							'Peli tallennettu. Julkaisun päivitys epäonnistui.',
							'warning',
						);
						// Return the export anyway - save should always succeed
						return of(updatedExport);
					}),
				);
			}),
		);
	}

	// public DeleteAllExperiences() {
	// 	this.backendService.GetAllExperiences().subscribe({
	// 		next: (experiences: Experience[]) => {
	// 			if (experiences.length === 0) {
	// 				this.ClearExports();
	// 				return;
	// 			}

	// 			// Delete each experience
	// 			experiences.forEach((experience: Experience) => {
	// 				this.backendService
	// 					.DeleteExperience(experience.id)
	// 					.subscribe({
	// 						next: () => {
	// 							console.log(
	// 								`Experience ${experience.id} deleted successfully`,
	// 							);
	// 							this.DeleteExportFromLocal(experience.id);
	// 						},
	// 						error: (error: any) => {
	// 							console.error(
	// 								`Error deleting experience ${experience.id}:`,
	// 								error,
	// 							);
	// 						},
	// 					});
	// 			});
	// 		},
	// 		error: (error: any) => {
	// 			console.error('Error fetching experiences:', error);
	// 		},
	// 	});
	// }

	public ShareExport(
		shareProcess: ShareProcess,
		usedArticleIds: number[],
	): Observable<CreatedShare> {
		const sharedExp: ShareExperienceRequest = {
			id: shareProcess.exportData.id,
			name: shareProcess.exportData.name,
			expirationDate: shareProcess.expirationDate,
			externalItems: usedArticleIds,
		};
		return this.backendService.ShareExperience(sharedExp).pipe(
			map((response: CreatedShare | ErrorResponse) => {
				if ('uuid' in response) {
					// Add to sharedExperiences
					this.sharedExperiences.push(response as CreatedShare);
					this.OnSharedExperiencesChanged.emit(
						this.sharedExperiences,
					);

					return response as CreatedShare;
				}
				throw new Error(
					Array.isArray((response as ErrorResponse).errors)
						? (response as ErrorResponse).errors.join(', ')
						: 'Unknown error while sharing export',
				);
			}),
			catchError((error: any) => {
				console.error('Error adding export to database:', error);
				throw error;
			}),
		);
	}

	/**
	 * Creates a share for an already-saved export with the specified expiry timestamp.
	 * @param exportData The export to create a share for (must have an ID and name)
	 * @param expiryTimestamp Unix timestamp for expiry (-1 for no expiry)
	 * @param usedArticleIds Pre-filtered array of article IDs to include in the share (should only include image, audio, pdf types)
	 * @returns Observable that emits the created share
	 */
	public createShareForExport(
		exportData: Export,
		expiryTimestamp: number,
		usedArticleIds: number[],
	): Observable<CreatedShare> {
		if (exportData.id === undefined) {
			return throwError(
				() => new Error('Export data must have an ID'),
			);
		}

		// Create the share request
		const shareRequest: ShareExperienceRequest = {
			id: exportData.id,
			name: exportData.name,
			expirationDate: expiryTimestamp,
			externalItems: usedArticleIds,
		};

		// Call backend to create the share
		return this.backendService.ShareExperience(shareRequest).pipe(
			map((response: CreatedShare | ErrorResponse) => {
				if ('uuid' in response) {
					// Add to sharedExperiences
					this.sharedExperiences.push(response as CreatedShare);
					this.OnSharedExperiencesChanged.emit(
						this.sharedExperiences,
					);
					return response as CreatedShare;
				}
				throw new Error(
					Array.isArray((response as ErrorResponse).errors)
						? (response as ErrorResponse).errors.join(', ')
						: 'Failed to create share',
				);
			}),
			catchError((error: unknown) => {
				console.error('Error creating share:', error);
				return throwError(() => error);
			}),
		);
	}

	/**
	 * Extracts and filters article IDs from gameData to include only external items (images, audio, PDFs).
	 * This is used when creating or updating shares to determine which external files need to be packaged.
	 * @param gameData The game data object to extract article IDs from
	 * @returns Filtered array of article IDs containing only image, audio, and pdf types
	 */
	public extractUsedArticleIds(gameData: GameData): number[] {
		// Get all article IDs from gameData recursively
		const allArticleIds = Array.from(
			new Set(
				gameData?.GetArticlesContainedInGameDataObjectsRecursively(
					true,
				) || [],
			),
		);

		// Filter to only include external items (images, audio, PDFs)
		const usedArticleIds =
			this.sourceMaterialService.filterExternalArticleIds(allArticleIds);

		return usedArticleIds;
	}

	/**
	 * Gets the primary (most recent) share for an export.
	 * @param exportId The ID of the export to get the primary share for
	 * @returns The primary share, or null if no shares exist for this export
	 */
	public getPrimaryShare(exportId: number): CreatedShare | null {
		const shares = this.GetSharedExperiencesForExport(exportId);
		if (shares.length === 0) {
			return null;
		}

		// Sort by creation date descending and return the most recent
		shares.sort((a, b) => (b.creationDate || 0) - (a.creationDate || 0));
		return shares[0];
	}

	/**
	 * Updates an existing shared experience with new game data and article IDs.
	 * This is used when an export is saved to automatically update its published share.
	 * @param share The existing share to update
	 * @param exportData The export containing updated gameData
	 * @param usedArticleIds Filtered array of external article IDs (images, audio, PDFs only)
	 * @returns Observable of the updated CreatedShare
	 */
	public updateSharedExperience(
		share: CreatedShare,
		exportData: Export,
		usedArticleIds: number[],
	): Observable<CreatedShare> {
		// Create the share request with updated data
		const shareRequest: ShareExperienceRequest = {
			id: exportData.id,
			name: exportData.name,
			expirationDate: share.expirationDate, // Keep existing expiration date
			externalItems: usedArticleIds,
		};

		// Call backend to update the share
		return this.backendService
			.UpdateSharedExperience(share.uuid, shareRequest)
			.pipe(
				map((response: CreatedShare | ErrorResponse) => {
					if ('uuid' in response) {
						// Update the share in local sharedExperiences array
						const index = this.sharedExperiences.findIndex(
							(s) => s.id === share.id,
						);
						if (index > -1) {
							this.sharedExperiences[index] =
								response as CreatedShare;
							this.OnSharedExperiencesChanged.emit(
								this.sharedExperiences,
							);
						}

						console.log(
							`Share ${share.uuid} updated successfully`,
						);
						return response as CreatedShare;
					}

					throw new Error(
						Array.isArray((response as ErrorResponse).errors)
							? (response as ErrorResponse).errors.join(', ')
							: 'Failed to update share',
					);
				}),
				catchError((error: unknown) => {
					console.error(
						`Error updating share ${share.uuid}:`,
						error,
					);
					return throwError(() => error);
				}),
			);
	}
}
